import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  TableToolbarMenu,
  TableToolbarAction,
  Button,
  Modal,
  Dropdown,
  Pagination,
  Grid,
  Column,
  MultiSelect,
  InlineLoading,
  InlineNotification,
  DatePicker,
  DatePickerInput,
  Checkbox,
  ComboBox,
  Toggle,
  TextInput,
} from "@carbon/react";
import { Delete, Filter,Add} from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";
import { useUser } from "../../../context/UserContext";

const headers = [
  { key: "planName", header: "Plan Name" },
  { key: "userName", header: "User Name" },
  { key: "name", header: "Name" },
  { key: "email", header: "Email" },
  { key: "contact", header: "Contact No" },
  { key: "startDate", header: "Start Date" },
  { key: "endDate", header: "End Date" },
  { key: "status", header: "Status" },
  { key: "autoRenew", header: "Auto Renew" },
  { key: "createdAt", header: "Created At" },
];

export default function UserSubscription() {
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const { user } = useUser();

  // Form states – dates are Date objects or null
  const [formData, setFormData] = useState({
    userId: "",
    subscriptionPlanId: "",
    startDate: null,
    endDate: null,
    status: "Active",
    autoRenew: false,
    propertyPaymentId: "",
  });

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
const [filters, setFilters] = useState({
  status: null,
  autoRenew: null,
  planName: [],
  startDate: "",   // was null
  endDate: "",     
});

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    status: [    { id: "Active", text: "Active" },
    { id: "Expired", text: "Expired" },
    { id: "Cancelled", text: "Cancelled" },
    { id: "Pending", text: "Pending" },
    ],
    planName: [],
    autoRenew: [
      { id: true, text: "Yes" },
      { id: false, text: "No" },
    ],
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formError, setFormError] = useState("");

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // User ID from context
  const userId = user?.userId || 1;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Normalize subscription data
  const normalizeSubscription = useCallback((item) => ({
    id: String(item.USER_SUBSCRIPTION_ID),
    subscriptionId: item.USER_SUBSCRIPTION_ID,
    userId: item.FK_USER_ID,
    subscriptionPlanId: item.FK_SUBSCRIPTION_PLAN_ID,
    userName: item.username,
    name: item.name || "",
    email: item.email || "",
    contact: item.contact_number || "",
    planName: item.PLAN_NAME || "N/P",
    planCode: item.plan_code,
    planDescription: item.plan?.DESCRIPTION,
    startDate: item.START_DATE,
    endDate: item.END_DATE,
    status: item.STATUS,
    autoRenew: item.AUTO_RENEW,
    propertyPaymentId: item.FK_PROPERTY_PAYMENT_ID,
    price: item.plan?.PRICE || "0.00",
    currency: item.plan?.CURRENCY || "INR",
    durationDays: item.plan?.DURATION_DAYS,
    features: item.plan?.FEATURES_JSON || {},
    createdAt: item.CREATED_AT,
    updatedAt: item.UPDATED_AT,
  }), []);

  // Extract unique values for filters
  const extractUniqueValues = useCallback((data) => {
    const uniquePlanNames = Array.from(
      new Set(data.map(item => item.planName).filter(Boolean))
    ).map(name => ({ id: name, text: name }));
    
    return {
      planName: uniquePlanNames
    };
  }, []);
const extractUsers = (data) => {
  return Array.from(
    new Map(
      data.map((item) => [
        item.FK_USER_ID,
        {
          id: item.FK_USER_ID,
          text: `${item.name || ""} (${item.username})`,
        },
      ])
    ).values()
  );
};
 const extractPlans = (data) => {
  return Array.from(
    new Map(
      data.map(item => [
        item.FK_SUBSCRIPTION_PLAN_ID,
        {
          id: item.FK_SUBSCRIPTION_PLAN_ID,
          text: item.PLAN_NAME 
        }
      ])
    ).values()
  );
};

const formatDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};


  // Fetch subscriptions
const fetchSubscriptions = useCallback(async (queryParams = "") => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    const response = await callApi(
      "GET",
      `/user-subscriptions/${queryParams ? `?${queryParams}` : ""}`
    );

    const data = Array.isArray(response?.data)
      ? response.data
      : [];

    const formattedRows = data.map((item) =>
      normalizeSubscription(item)
    );

    if (!mountedRef.current) return;

    setRows(formattedRows);

    const plansList = extractPlans(data);
    setPlans(plansList);

    const usersList = extractUsers(data);
    setUsers(usersList);

    const filterOptions = extractUniqueValues(formattedRows);

    setFilterOptions((prev) => ({
      ...prev,
      planName: filterOptions.planName,
    }));

  } catch (err) {
    console.error("fetchSubscriptions error:", err);

    if (!mountedRef.current) return;

    setError("Unable to load subscriptions. Please try again.");
  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
}, [
  normalizeSubscription,
  extractUniqueValues,
]);

const applyFilters = async () => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    const queryParams = new URLSearchParams();

    // Status
    if (filters.status?.id) {
      queryParams.append("status", filters.status.id);
    }

    // Plan Name
    if (filters.planName?.length > 0) {
      filters.planName.forEach((plan) => {
        queryParams.append("plan_name", plan.id);
      });
    }

    // Auto Renew
    if (
      filters.autoRenew !== null &&
      filters.autoRenew !== undefined
    ) {
      queryParams.append(
        "auto_renew",
        filters.autoRenew.id
      );
    }

// Start Date
if (filters.startDate) {
  queryParams.append("start_date", filters.startDate);
}

// End Date
if (filters.endDate) {
  queryParams.append("end_date", filters.endDate);
}

    console.log(
      "User Subscription Filter URL:",
      queryParams.toString()
    );

    const response = await callApi(
      "GET",
      `/user-subscriptions/?${queryParams.toString()}`
    );

    const data = Array.isArray(response?.data)
      ? response.data
      : [];

    const formattedRows = data.map((item) =>
      normalizeSubscription(item)
    );

    setRows(formattedRows);
    setPage(1);

  } catch (error) {
    console.error("Failed to filter subscriptions:", error);

    setError("Failed to load filtered subscriptions.");
    setRows([]);

  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
};

  // Initial data fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchSubscriptions();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchSubscriptions]);

  // Filter rows based on search and filters
const filteredRows = useMemo(() => {
  let filtered = [...rows];

  if (debouncedSearch) {
    const search = debouncedSearch;

    filtered = filtered.filter((row) => {
      return (
        row.planName?.toLowerCase().includes(search) ||
        row.userName?.toLowerCase().includes(search) ||
        row.name?.toLowerCase().includes(search) ||
        row.email?.toLowerCase().includes(search) ||
        row.contact?.toLowerCase().includes(search) ||
        row.status?.toLowerCase().includes(search) ||
        row.startDate?.toLowerCase().includes(search) ||
        row.endDate?.toLowerCase().includes(search) ||
        row.createdAt?.toLowerCase().includes(search) ||
        String(row.subscriptionId || "").includes(search)
      );
    });
  }

  return filtered.sort(
    (a, b) => b.subscriptionId - a.subscriptionId
  );
}, [rows, debouncedSearch]);

  const paginatedRows = useMemo(() =>
    filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize]
  );

  // Handle filter changes
  const handleFilterChange = (filterName, selectedItems) => {
    setFilters(prev => ({ ...prev, [filterName]: selectedItems }));
  };

  const handleAutoRenewFilterChange = (selectedItem) => {
    setFilters(prev => ({ ...prev, autoRenew: selectedItem }));
  };

const clearAllFilters = async () => {
  setFilters({
    status: null,
    autoRenew: null,
    planName: [],
    startDate: "",
    endDate: "",
  });

  setPage(1);
  await fetchSubscriptions();
};

  const activeFilterCount = Object.values(filters).reduce((count, filter) => {
    if (Array.isArray(filter)) return count + filter.length;
    if (filter !== null && filter !== undefined && filter !== "") return count + 1;
    return count;
  }, 0);

  // Reset modal
  const resetModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setEditingRow(null);
    setFormData({
      userId: "",
      subscriptionPlanId: "",
      startDate: null,
      endDate: null,
      status: "Active",
      autoRenew: false,
      propertyPaymentId: "",
    });
    setValidationErrors({});
    setFormError("");   // <-- ADD THIS LINE
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.userId) {
      errors.userId = "User is required";
    }

    if (!formData.subscriptionPlanId) {
      errors.subscriptionPlanId = "Subscription Plan is required";
    }

    if (!formData.startDate) {
      errors.startDate = "Start Date is required";
    }

    if (!formData.endDate) {
      errors.endDate = "End Date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      formData.endDate < formData.startDate
    ) {
      errors.endDate = "End Date must be after Start Date";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSave = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    setFormError("Please fill all required fields.");
    return;
  }

  setFormError("");

  try {
    let payload;

    if (isEdit && editingRow) {
      const startDateStr = formData.startDate
        ? formData.startDate.toISOString().split("T")[0]
        : "";
      const endDateStr = formData.endDate
        ? formData.endDate.toISOString().split("T")[0]
        : "";

      payload = {
        USER_SUBSCRIPTION_ID: editingRow.id,
        FK_USER_ID: formData.userId,
        FK_SUBSCRIPTION_PLAN_ID: formData.subscriptionPlanId,
        START_DATE: startDateStr,
        END_DATE: endDateStr,
        STATUS: formData.status,
        AUTO_RENEW: formData.autoRenew,
        FK_PROPERTY_PAYMENT_ID: formData.propertyPaymentId || null,
        UPDATED_BY_USER_ID: userId,
      };

      await callApi("PUT", `/user-subscriptions/${editingRow.id}`, payload);
      setSuccess("Subscription updated successfully.");
    } else {
      //  NEW ADMIN API PAYLOAD
      payload = {
        FK_USER_ID: formData.userId,
        FK_SUBSCRIPTION_PLAN_ID: formData.subscriptionPlanId,
        AUTO_RENEW: formData.autoRenew,
        FK_PROPERTY_PAYMENT_ID: formData.propertyPaymentId || 0,
        STATUS: formData.status || "Active"
      };

      await callApi("POST", "/user-subscriptions/admin", payload);
      setSuccess("Subscription added successfully.");
    }

    await fetchSubscriptions();
    resetModal();
   } catch (error) {
    console.error("Failed to save:", error);

    const backendMessage =
      error?.data?.detail ||
      error?.responseData?.data?.detail ||
      error?.responseData?.data?.message ||
      error?.responseData?.message ||
      error?.message;

    setFormError(
      backendMessage && backendMessage !== "Error"
        ? backendMessage
        : "Failed to save subscription. Please try again."
    );
  }
};

  const handleAddNew = () => {
    setIsEdit(false);
    setEditingRow(null);
    setFormData({
      userId: "",
      subscriptionPlanId: "",
      startDate: null,
      endDate: null,
      status: "Active",
      autoRenew: false,
      propertyPaymentId: "",
    });
    setFormError("");   // <-- ADD THIS LINE
    setIsModalOpen(true);
  };

  // Edit subscription – convert date strings to Date objects
  const handleEdit = (row) => {
    const selected = rows.find((r) => r.id === row.id);
    setIsEdit(true);
    setEditingRow(selected);
    setFormData({
      userId: selected.userId,
      subscriptionPlanId: selected.subscriptionPlanId,
      startDate: selected.startDate ? new Date(selected.startDate) : null,
      endDate: selected.endDate ? new Date(selected.endDate) : null,
      status: selected.status,
      autoRenew: selected.autoRenew,
      propertyPaymentId: selected.propertyPaymentId || "",
    });
    setFormError("");   // <-- ADD THIS LINE
    setIsModalOpen(true);
  };

  
  // Open delete modal
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // Delete subscription
  const handleConfirmDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await callApi("DELETE", `/user-subscriptions/${deleteId}`);
      await fetchSubscriptions();
      setSuccess("Subscription deleted successfully.");
    } catch (error) {
      console.error("Failed to delete subscription:", error.message);
      setError("Failed to delete subscription. Please try again.");
    } finally {
      setDeleting(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // Batch delete
  const handleBatchDelete = async (selectedRows) => {
    if (!selectedRows || selectedRows.length === 0) return;
    setDeleting(true);
    try {
      const deletePromises = selectedRows.map(row =>
        callApi("DELETE", `/user-subscriptions/${row.id}`)
      );
      await Promise.all(deletePromises);
      await fetchSubscriptions();
      setSuccess(`${selectedRows.length} subscription(s) deleted successfully.`);
    } catch (error) {
      console.error("Failed to delete subscriptions:", error.message);
      setError("Failed to delete subscriptions. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Toggle auto-renew
const handleToggleAutoRenew = async (row) => {
  try {
    await callApi("PUT", `/user-subscriptions/${row.id}/auto-renew`, {
      AUTO_RENEW: !row.autoRenew,
      UPDATED_BY_USER_ID: userId,
    });
    await fetchSubscriptions();
    setSuccess(`Auto-renew ${!row.autoRenew ? 'enabled' : 'disabled'} successfully.`);
  } catch (error) {
    console.error("Failed to toggle auto-renew:", error.message);
    setError("Failed to update auto-renew setting.");
  }
};

  return (
    <>
      <DataTable rows={paginatedRows} headers={headers}>
        {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => {
          const batchActionProps = getBatchActionProps();
          
          return (
            <TableContainer title="User Subscriptions">
  <TableToolbar>
    {selectedRows.length > 0 ? (
      <TableBatchActions {...batchActionProps}>
        <TableBatchAction
          renderIcon={Delete}
          onClick={() => handleBatchDelete(selectedRows)}
        >
          Delete ({selectedRows.length})
        </TableBatchAction>
      </TableBatchActions>
    ) : (
      <TableToolbarContent>

        <TableToolbarSearch
          persistent
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search user subscriptions..."
        />
 <Button
          kind="ghost"
          renderIcon={Filter}
          hasIconOnly
          iconDescription="Toggle filters"
          onClick={() => setShowFilters((v) => !v)}
        >
          {activeFilterCount > 0 && `(${activeFilterCount})`}
        </Button>
        <Button 
          renderIcon={Add} 
          kind="primary" 
          onClick={handleAddNew}
        >
          Add Subscription
        </Button>

       

      </TableToolbarContent>
    )}
  </TableToolbar>

              {/* Filter Section */}
              {showFilters && (
                <div style={{ width: "100%", padding: "1rem 1.25rem", background: "#f4f4f4", borderBottom: "1px solid #e0e0e0" }}>
                  <Grid narrow>
                    <Column lg={3} md={4} sm={4}>
                      <Dropdown
  id="filter-status"
  titleText="Status"
  label="Choose"
  items={filterOptions.status}
  itemToString={(item) => item?.text || ""}
  selectedItem={filters.status}
  onChange={({ selectedItem }) =>
    setFilters(prev => ({
      ...prev,
      status: selectedItem,
    }))
  }
/>
                    </Column>
                    <Column lg={3} md={4} sm={4}>
                      <MultiSelect
                        id="filter-plan-name"
                        label="Choose"
                        titleText="Plan Name"
                        items={filterOptions.planName}
                        itemToString={(item) => item?.text || ""}
                        selectedItems={filters.planName}
                        onChange={({ selectedItems }) => handleFilterChange("planName", selectedItems)}
                      />
                    </Column>
                    <Column lg={2} md={4} sm={4}>
                      <Dropdown
                        id="filter-auto-renew"
                        titleText="Auto Renew"
                        label="Select"
                        items={filterOptions.autoRenew}
                        itemToString={(item) => item?.text || ""}
                        selectedItem={filters.autoRenew}
                        onChange={({ selectedItem }) => handleAutoRenewFilterChange(selectedItem)}
                      />
                    </Column>
<Column lg={2} md={4} sm={4}>
  <TextInput
    id="filter-start-date"
    type="date"
    labelText="Start Date"
    value={filters.startDate}
    onChange={(e) =>
      setFilters((prev) => ({ ...prev, startDate: e.target.value }))
    }
  />
</Column>

<Column lg={2} md={4} sm={4}>
  <TextInput
    id="filter-end-date"
    type="date"
    labelText="End Date"
    value={filters.endDate}
    onChange={(e) =>
      setFilters((prev) => ({ ...prev, endDate: e.target.value }))
    }
  />
</Column>
                    <Column lg={16} style={{ marginTop: "1rem" }}>
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      gap: "0.75rem",
    }}
  >
    <Button
      kind="secondary"
      size="sm"
      onClick={clearAllFilters}
    >
      Clear All
    </Button>

    <Button
      kind="primary"
      size="sm"
      onClick={applyFilters}
    >
      Search
    </Button>
  </div>
</Column>
                  </Grid>
                </div>
              )}

              {loading && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <InlineLoading description="Loading subscriptions..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <th />
                          {headers.map((header) => {
                    const { key, ...headerProps } = getHeaderProps({ header });

                    return (
                      <TableHeader key={key} {...headerProps}>
                        {header.header}
                      </TableHeader>
                    );
                  })}
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                        {searchTerm || activeFilterCount > 0 
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No subscriptions found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                   paginatedRows.map((row) => {
                      const { key, ...rowProps } = getRowProps({ row });

                      return (
                        <TableRow key={key} {...rowProps}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        <TableCell>
                          <div>
                            <strong>{row.planName}</strong>
                            {row.planCode && (
                              <div style={{ fontSize: '12px', color: '#6f6f6f' }}>{row.planCode}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{row.userName}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.contact}</TableCell>
<TableCell>{formatDateTime(row.startDate)}</TableCell>
<TableCell>{formatDateTime(row.endDate)}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {row.autoRenew ? 'Yes' : 'No'}
                            <Toggle
                              id={`auto-renew-${row.id}`}
                              size="sm"
                              toggled={row.autoRenew}
                              onToggle={() => handleToggleAutoRenew(row)}
                              labelText=""
                              hideLabel
                            />
                          </span>
                        </TableCell>
                       
                       <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                        <TableCell>
                          <TableToolbarMenu>
                            <TableToolbarAction onClick={() => handleEdit(row)}>Edit</TableToolbarAction>
                            <TableToolbarAction onClick={() => confirmDelete(row.id)}>Cancel</TableToolbarAction>
                          </TableToolbarMenu>
                        </TableCell>
                        </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[10, 20, 30, 50]}
            onChange={({ page: p, pageSize: ps }) => { setPage(p); setPageSize(ps); }}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        modalHeading={isEdit ? "Edit Subscription" : "Add Subscription"}
        primaryButtonText={isEdit ? "Update" : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={resetModal}
        onRequestSubmit={handleSave}
      >
        {formError && (
          <InlineNotification
            kind="error"
            title="Cannot save subscription"
            subtitle={formError}
            lowContrast
            onClose={() => setFormError("")}
            style={{ marginBottom: "1rem" }}
          />
        )}

        <div style={{ marginBottom: "1rem" }}>
          <ComboBox
            id="user-select"
            titleText="User *"
            placeholder="Select user"
            items={users}
            itemToString={(item) => item?.text || ""}
            selectedItem={users.find(u => u.id === formData.userId) || null}
            onChange={({ selectedItem }) => {
              handleInputChange("userId", selectedItem?.id || "");
            }}
          />
          {validationErrors.userId && (
            <div style={{ color: "#da1e28", fontSize: "0.75rem", marginTop: "0.25rem" }}>
              {validationErrors.userId}
            </div>
          )}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <ComboBox
            id="plan-select"
            titleText="Plan Name *"
            placeholder="Select plan"
            items={plans}
            itemToString={(item) => item?.text || ""}
            selectedItem={plans.find(p => p.id === formData.subscriptionPlanId) || null}
            onChange={({ selectedItem }) => {
              handleInputChange("subscriptionPlanId", selectedItem?.id || "");
            }}
          />
          {validationErrors.subscriptionPlanId && (
            <div style={{ color: "#da1e28", fontSize: "0.75rem", marginTop: "0.25rem" }}>
              {validationErrors.subscriptionPlanId}
            </div>
          )}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <DatePicker
            datePickerType="single"
                        value={formData.startDate}
            onChange={([date]) => handleInputChange("startDate", date || null)}
          >
            <DatePickerInput
              id="start-date"
              labelText="Start Date *"
              placeholder="mm/dd/yyyy"
              invalid={!!validationErrors.startDate}
              invalidText={validationErrors.startDate}
            />
          </DatePicker>
        </div>
        
        <div style={{ marginBottom: "1rem" }}>
          <DatePicker
            datePickerType="single"
                        value={formData.endDate}
            onChange={([date]) => handleInputChange("endDate", date || null)}
          >
            <DatePickerInput
              id="end-date"
              labelText="End Date *"
              placeholder="mm/dd/yyyy"
              invalid={!!validationErrors.endDate}
              invalidText={validationErrors.endDate}
            />
          </DatePicker>
        </div>
        
        <div style={{ marginBottom: "1rem" }}>
          <Checkbox
            id="auto-renew"
            labelText="Auto Renew"
            checked={formData.autoRenew}
            onChange={(e) => handleInputChange("autoRenew", e.target.checked)}
          />
        </div>
        </Modal>


        
      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Cancel"
        primaryButtonText={deleting ? <InlineLoading description="Cancelling..." /> : "Cancel"}
        secondaryButtonText="Close"
        danger
        onRequestClose={() => { if (!deleting) setIsDeleteModalOpen(false); }}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={deleting}
      >
        <p>Are you sure you want to cancel this subscription? This action cannot be undone.</p>
      </Modal>
    </>
  );
}